/**
 * Users API service for Whakahaere Safety Management System
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 */

import { client } from '../lib/http-client';
import { UserFormInitialValues } from '../types/UserFormInitialValue';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export type UserStatus = 'all' | 'active' | 'inactive';

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  cell_phone?: string;
  district?: number | null;
  groups?: number[];
  is_vip?: boolean;
  is_role_user?: boolean;
  is_locked?: boolean;
  is_removed?: boolean;
  disable_sso?: boolean;
  current_school?: number | null;
  vendor_id?: number | null;
  vendor_name?: string | null;
  has_elevated_sms_access?: boolean;
  permissions?: string[];
  language?: string | null;
}

export interface UserListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserFormInitialValues[];
}

export interface UserFilters {
  search?: string;
  name?: string;
  email?: string;
  is_active?: boolean;
  is_vendor_user?: boolean;
  page?: number;
  page_size?: number;
  limit?: number;
  offset?: number;
  ordering?: string;
  ids?: string;
}

export type UserSessionLog = {
  id: number;
  user_email: string;
  user_name: string;
  device: string;
  status: 'success' | 'failed';
  user_agent: string;
  client_version: string | null;
  created_at: string;
  updated_at: string;
  ip: string;
  jwt: string;
  session_key: string;
  expiry_time: string | null;
  user: number;
};
export interface UserSessionLogResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserSessionLog[];
}

/**
 * Get list of users with optional filters
 */
export async function getUsers(
  filters: UserFilters = {},
): Promise<UserListResponse> {
  // Original used `query` for search; map back via openapi-fetch query.
  const query: Record<string, unknown> = { ...filters };
  if (filters.search) {
    query.query = filters.search;
    delete query.search;
  }
  const result = await client.GET('/api/v2/user-detailed-refactor/', {
    params: { query: query as never },
  });
  return unwrap(result) as unknown as UserListResponse;
}

/**
 * Get a single user by ID
 */
export async function getUserById(id: number): Promise<User> {
  const result = await client.GET('/api/v2/user/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as User;
}

/**
 * Create a new user
 */
export async function createUser(
  data: Omit<User, 'id'> & { password: string; confirm_password: string },
): Promise<User> {
  const result = await client.POST('/api/v2/user/', {
    body: data as never,
  });
  return unwrap(result) as unknown as User;
}

/**
 * Update an existing user
 */
export async function updateUser(
  id: number,
  data: Partial<Omit<User, 'id'>>,
): Promise<User> {
  const result = await client.PATCH('/api/v2/user/{id}/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as User;
}

/**
 * Upload a profile photo (avatar-editor parity with AIM).
 * Multipart PATCH: append the file and let the browser set the
 * Content-Type boundary; openapi-fetch passes FormData through to ky.
 */
export async function uploadProfilePhoto(
  id: number,
  file: File,
): Promise<User> {
  const formData = new FormData();
  formData.append('profile_photo', file);
  const result = await client.PATCH('/api/v2/user/{id}/', {
    params: { path: { id } as never },
    body: formData as unknown as never,
  });
  return unwrap(result) as unknown as User;
}

/**
 * Remove the profile photo: PATCH `profile_photo: null`.
 */
export async function removeProfilePhoto(id: number): Promise<User> {
  const result = await client.PATCH('/api/v2/user/{id}/', {
    params: { path: { id } as never },
    body: { profile_photo: null } as never,
  });
  return unwrap(result) as unknown as User;
}

/**
 * Delete a user (soft delete)
 */
export async function deleteUser(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/user/{id}/', {
    params: { path: { id } as never },
  });
  unwrap(result);
}

/**
 * Toggle user active status
 */
export async function toggleUserStatus(
  id: number,
  is_active: boolean,
): Promise<User> {
  return updateUser(id, { is_active });
}

export async function getUseractivitylog(
  id: number,
): Promise<UserSessionLogResponse> {
  const result = await client.GET('/api/v2/userloginactivity/', {
    params: { query: { limit: 20, offset: 0, user: id } as never },
  });
  return unwrap(result) as unknown as UserSessionLogResponse;
}

export async function deleteDevice(tokenId: string): Promise<void> {
  // /api/v2/fcm-token/{id}/ — verify in schema; otherwise client.ky.
  const result = await client.DELETE('/api/v2/fcm-token/{id}/', {
    params: { path: { id: tokenId } as never },
  });
  unwrap(result);
}

export interface BulkInviteRowResult {
  index: number;
  status: 'created' | 'skipped' | 'error';
  email: string;
  reason?: string;
}

export interface BulkInviteUploadResponse {
  results: BulkInviteRowResult[];
  summary: {
    created: number;
    skipped: number;
    error: number;
    total: number;
  };
}

/**
 * Upload a CSV or XLSX file containing bulk user invites.
 */
export async function uploadBulkInvite(file: File): Promise<BulkInviteUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await client.ky.post('api/v2/user/bulk-invite-upload/', {
    body: formData,
  });
  return (await response.json()) as BulkInviteUploadResponse;
}

export interface BulkInviteRowPayload {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  site: string;
}

/**
 * Submit a list of user invites via JSON bulk-form.
 */
export async function submitBulkInvite(rows: BulkInviteRowPayload[]): Promise<BulkInviteUploadResponse> {
  const response = await client.ky.post('api/v2/user/bulk-invite/', {
    json: rows,
  });
  return (await response.json()) as BulkInviteUploadResponse;
}
